import { type FC } from "react";
import { Dialog } from "@mui/material";
import { getLuminance } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import {
  useUpdateWidgetMutation,
  type WidgetsItem,
  type WidgetFormValues,
} from "@/partner/entities/widgets";
import { WidgetForm } from "./WidgetForm";

interface EditWidgetDialogProps {
  open: boolean;
  widget: WidgetsItem | null;
  onClose: () => void;
  onCompleted: () => void;
}

export const EditWidgetDialog: FC<EditWidgetDialogProps> = ({
  open,
  widget,
  onClose,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [updateWidget, { loading }] = useUpdateWidgetMutation({
    refetchQueries: ["WidgetsList"],
  });

  if (!widget) return null;

  const handleSubmit = async (values: WidgetFormValues) => {
    const buttonTextColor =
      getLuminance(values.buttonColor) > 0.5 ? "#000000" : "#ffffff";

    await updateWidget({
      variables: {
        id: widget.id,
        input: { ...values, buttonTextColor },
      },
      onCompleted: () => {
        enqueueSnackbar(t("widgets.edit.success"), { variant: "success" });
        onCompleted();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    });
  };

  return (
    <Dialog fullScreen open={open} onClose={onClose}>
      <WidgetForm
        title={t("widgets.edit.title")}
        loading={loading}
        initialValues={{
          title: widget.title,
          buttonUrl: widget.buttonUrl,
          buttonText: widget.buttonText,
          buttonColor: widget.buttonColor,
        }}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
